/**
 * What the SPA's Sentry never sends: a desktop link (slice 6c §6), which the
 * Watch sheet's iframe holds, and an invite's token (slice 6a §3.4). The
 * join page's URL (`/hq/join/<token>`) is an event's `request.url`, and its
 * `/api/join/<token>` calls and the navigation to it are breadcrumbs. Each
 * keeps its shape with the token replaced, as `worker/lib/sentry-scrub.ts`
 * does on the Worker's side.
 */
import type { Breadcrumb, ErrorEvent } from "@sentry/react";

const redactJoinToken = (text: string) =>
  text.replace(/(\/join\/)[A-Za-z0-9_-]{16,}/g, "$1[token]");

/**
 * Boat's stream page keeps its host; the mock one its path
 * (`worker/box/redact-desktop.ts`); Kernel's Live View (slice 6l) its host.
 */
const redactDesktop = (text: string) =>
  text
    .replace(/(https?:\/\/[^\s"'\\/]*\.on\.boat\.dev)[^\s"'\\]*/g, "$1/[desktop]")
    .replace(
      /(https?:\/\/[^\s"'\\/]*\.(?:onkernel\.com|kernel\.sh)(?::\d+)?)[^\s"'\\]*/g,
      "$1/[live]",
    )
    .replace(/(\/api\/computer\/mock-desktop)[?#][^\s"'\\]*/g, "$1[desktop]");

const scrub = <T>(value: T): T =>
  JSON.parse(redactDesktop(redactJoinToken(JSON.stringify(value)))) as T;

/** An event, the token taken out of its URL, breadcrumbs and messages. */
export const scrubEvent = (event: ErrorEvent): ErrorEvent => scrub(event);

/** A breadcrumb (a navigation, a fetch), the token taken out. */
export const scrubBreadcrumb = (crumb: Breadcrumb): Breadcrumb => scrub(crumb);
